import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CardContent from '@material-ui/core/CardContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import BlueFullWidthButton from 'components/BlueFullWidthButton/BlueFullWidthButton';
import styled from 'styled-components/macro';

import Company from '../../../../types/Company';
import api from '../../../../utils/api';
import { useDialog } from '../../../../utils/dialog';
import { downloadFile } from '../../../../utils/download';
import paperRoll from '../../assets/img8.png';
import { StyledCard, StyledTitle } from './styled';

interface Props {
    company: Company;
    updateData: () => void;
}

const StyledImg = styled.img`
    & {
        filter: brightness(100%);
        transition: filter 0.4s ease;
    }

    &:hover {
        filter: brightness(85%);
    }

    &:active {
        filter: brightness(75%);
        transition: filter 0.01s ease;
    }
`;

const Card7 = (props: Props) => {
    const { company } = props;
    const { chartData } = company;
    const { pfcStandardDocumentId } = chartData;

    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);

    const [openDialog, closeDialog] = useDialog();

    const base64toBlob = useCallback((dataURI: string) => {
        // convert a base64 string to a blob object
        const byteString = atob(dataURI);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return new Blob([ab]);
    }, []);

    const download = useCallback(() => {
        if (!pfcStandardDocumentId) {
            openDialog({
                title: t('dialogs.generic.error'),
                message: t('pages.dashboard.cards.standard.error'),
                actions: [
                    {
                        text: t('dialogs.generic.close'),
                        color: 'primary',
                        action: closeDialog
                    },
                ]
            });
            return;
        }

        // fetch the file content, decode the base64 and download it
        const timeout = setTimeout(() => {
            setIsLoading(true);
        }, 150);

        api.request('/public_documents/' + pfcStandardDocumentId).then(res => {
            const { documentName, extension, content } = res;
            const fileName = documentName.replace(/\W+/g, '_');
            const blob = base64toBlob(content);

            downloadFile(blob, fileName + '.' + extension);
        }).finally(() => {
            setIsLoading(false);
            clearTimeout(timeout);
        });
    }, [base64toBlob, pfcStandardDocumentId, setIsLoading, closeDialog, openDialog, t]);

    return (
        <StyledCard variant='outlined'>
            <div style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: 'transparent' }}>
                {isLoading && (
                    <div style={{ zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'absolute', width: 'calc(100% + 12px)', height: 'calc(100% + 12px)', margin: '-6px', backgroundColor: 'rgba(255, 255, 255, 0.8)', pointerEvents: 'all' }}>
                        <CircularProgress />
                    </div>
                )}

                <CardContent style={{ height: '100%' }}>
                    <div style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
                        <StyledTitle style={{ fontSize: '1.65rem', fontWeight: 500, marginBottom: '4px' }}>
                            <span>{t('pages.dashboard.cards.standard.title')}</span>
                        </StyledTitle>

                        <StyledTitle style={{ fontSize: '1rem' }}>
                            {t('pages.dashboard.cards.standard.description')}
                        </StyledTitle>

                        <StyledImg src={paperRoll} style={{ maxWidth: '100%', maxHeight: '120px', display: 'block', margin: '24px auto 12px', cursor: 'pointer' }} onClick={download} />

                        <div style={{ flexGrow: 1 }} />

                        <BlueFullWidthButton variant='contained' color='primary' style={{ width: 'calc(100% + 44px)', margin: '24px -22px -18px' }} onClick={download} disabled={!chartData.currentAssessmentId}>
                            {t('pages.dashboard.cards.standard.button')}
                        </BlueFullWidthButton>
                    </div>
                </CardContent>
            </div>
        </StyledCard>
    );
};

export default Card7;
